import { topicPosterContentSlots, topicPosterSurfaceMarkup } from '../../../shared/topicPosterContent';
import { topicCtaLoadingMarkup } from '../../../shared/playerIcons';

/**
 * Topic detail view markup. The previous markup burned in a complete
 * fake LLM topic:
 *   - H1 "How LLMs Actually Work"
 *   - description, 8 chapters, 45 min audio, 2 video, 1247 readers
 *   - 4 hand-written "What you'll learn" bullets
 *   - 6 hardcoded premium chapter chips
 *   - "From $4.99/mo" marketing string
 *
 * All of those are now empty placeholders filled by
 * `topicDetailHydrator` from /api/topics/:slug and a sibling
 * /api/topics/:slug/chapters. The marketing copy on the
 * premium-unlock section comes from siteSettings.
 */
const topicLoadingPosterFields = {
	title: '',
	subtitle: '',
	titleTag: 'h1' as const,
	titleClass: 'tdb-title',
	subtitleClass: 'tdb-desc',
	metricVariant: 'topic-hero' as const,
	categoryVariant: 'topic-hero' as const,
	loading: true,
	ids: {
		title: 'topic-title',
		subtitle: 'topic-desc',
		metrics: 'topic-hero-metrics',
		chapters: 'topic-chapters',
		duration: 'topic-duration',
		category: 'topic-hero-pills',
		primaryCategory: 'topic-cat-pill',
		like: 'topicLikeBtn',
		dislike: 'topicDislikeBtn',
		favorite: 'topicFavBtn',
	},
	bindings: { title: 'topic.title', subtitle: 'topic.description' },
};

// R5 PC hero: the footer categories ride inside a badges row that also carries
// the row-mounted cover-badge twin. Since R8 the PC hero starts at >=641;
// outside it the wrapper is display:contents, so the phone layout is unchanged.
const topicHeroBadgesMarkup = (categoriesMarkup: string): string =>
	`<div class="topic-hero-badges"><span class="cover-badge topic-cover-badge topic-cover-badge--row" id="topic-cover-badge-row" hidden></span>${categoriesMarkup}</div>`;

const topicLoadingPoster = topicPosterSurfaceMarkup({
	poster: topicLoadingPosterFields,
	coverId: 'topic-banner-bg',
	coverClass: 'topic-cover topic-cover--default',
	contentClass: 'tdb-content',
	copyClass: 'tdb-primary',
	footerClass: 'topic-hero-footer',
	badgeMarkup: '<span class="cover-badge topic-cover-badge" id="topic-cover-badge" hidden></span>',
	categoryMarkup: topicHeroBadgesMarkup(topicPosterContentSlots(topicLoadingPosterFields).categories),
});

export const topicMarkup = String.raw`<!-- ====== TOPIC DETAIL VIEW ====== -->
    <div class="view" id="view-topic">
      <div class="topic-back-row">
        <button class="btn btn-default btn-sm topic-back-pill" data-proto-click="backFromTopic()">
          <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </button>
      </div>
      <div class="topic-detail-banner topic-hero-layout--split topic-hero-shell">
        <div class="topic-poster-surface topic-poster-surface--cover" id="topic-cover">${topicLoadingPoster}</div>
        <div class="tdb-actions topic-hero-cta-extension" id="topic-hero-actions" data-hero-loading-slot="primary-action" style="display:flex;align-items:center;gap:10px;">
			<a class="btn btn-outline topic-action-pill topic-primary-cta loading" id="topicPrimaryCta" aria-busy="true" aria-label="Loading next chapter">
			  ${topicCtaLoadingMarkup()}
			</a>
            <div class="topic-cta" id="topicPrimaryCtaCard" hidden><a class="topic-cta-copy" id="topicPrimaryCtaLink" href="#"><span class="topic-cta-kicker" id="topicPrimaryCtaKicker"></span><span class="topic-cta-title" id="topicPrimaryCtaTitle"></span></a><span class="topic-cta-dur" id="topicPrimaryCtaDur" hidden></span><button class="topic-cta-play" id="topicPrimaryCtaPlay" type="button" aria-label="Play chapter"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg></button></div>
        </div>
        <div class="topic-promise-card" id="topic-outcomes-block" data-hero-loading-slot="keywords">
            <div class="topic-promise-kicker">Keywords</div>
            <div class="topic-keyword-cloud" id="topic-keyword-cloud" data-hero-loading-slot="keyword-cloud"></div>
        </div>
      </div>

      <div class="topic-info-band" id="topic-info-band" hidden>
        <details class="topic-disclosure topic-summary topic-info-panel" id="topic-summary-block" hidden open>
          <summary>Overview</summary>
          <p id="topic-summary"></p>
        </details>
        <details class="topic-disclosure key-takeaways topic-info-panel" id="topic-takeaways-block" hidden>
          <summary>Learning outcomes</summary>
          <ul></ul>
        </details>
        <details class="topic-disclosure topic-keywords topic-info-panel" id="topic-keywords-mobile-block" hidden>
          <summary>Keywords</summary>
          <div class="topic-keyword-cloud topic-keyword-cloud--mobile" id="topic-keyword-cloud-mobile"></div>
        </details>
      </div>

      <div class="article-list" id="topicChapterList">
        <h3 class="heading-3 mb-24">Chapters</h3>
        <p id="topicChapterSubtitle" hidden data-bind="topic.chapterSubtitle"></p>
        <div id="topicChapterItems"></div>
      </div>
      <!-- What Premium Unlocks (hidden by default; shown only to visitor / free by the controller) -->
      <div class="premium-preview" id="topicPremiumUnlocks" style="display: none;">
        <div class="pp-header">
          <h4 id="topicPremiumHeadline" data-bind="topic.premiumHeadline">Unlock premium chapters</h4>
          <a data-proto-click="showPaywall()">Plans</a>
        </div>
        <div class="pp-chapters" id="topicPremiumPills">
          <span class="loading-placeholder">Loading chapter chips…</span>
        </div>
      </div>
      <div id="topic-faq-block"></div>
    </div>

    `;
